<template>
  <div>
    <app-spinner v-if="!is_loaded"/>
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1
            @click="onCopyToClipboard()"
            class="title title--hovered"
            style="cursor: pointer"
          >
            {{ form.hotel.name }} (#{{ $route.params.id }})
          </h1>
          <div v-if="form.lead.reservation_manager" class="manager-title">
            <div class="manager-title__photo">
              <img :src="getUserAvatar(form.lead.reservation_manager.crop.crop_url)" alt="Avatar">
            </div>
            <span class="manager-title__name">{{ form.lead.reservation_manager.full_name }}</span>
          </div>
          <app-option-picker
            @click="onOptionSelected($event, true)"
            v-model="form.reservation_status"
            :options="form.available_reservation_statuses"
            :disable="form.available_reservation_statuses.length < 1 || (!is_general_manager && !is_reservations_department)"
          />
          <div v-if="form.lead" class="manager-title">
            <div class="manager-title__photo">
              <img :src="getUserAvatar(form.lead.manager.crop.crop_url)" alt="Avatar">
            </div>
            <span class="manager-title__name">{{ form.lead.manager.full_name }}</span>
          </div>
          <app-option-picker
            @click="onOptionSelected($event, false)"
            v-model="form.sales_status"
            :options="form.available_sales_statuses"
            :disable="form.available_sales_statuses.length < 1 || (!is_general_manager && !is_sales_manager && !is_sales_supervisor)"
          />
        </app-cells>
        <app-cells :indent="false">
          <app-button
            v-if="is_general_manager || is_reservations_department"
            @click.native="onShowManagerPopup"
            theme="warn"
            size="small"
            ref="manager_popup"
          >
            {{ form.lead && form.lead.reservation_manager ? 'Change manager' : 'Assign manager' }}
          </app-button>
          <app-button v-if="is_general_manager" @click.native="$modal.show('accept-delete-res')" size="small"
                      theme="error">
            Delete
          </app-button>
          <a
            v-if="is_super_user"
            :href="`${admin_url}reservations/hotel/${$route.params.id}/change/`"
            target="_blank"
            class="btn btn--blue btn--small"
          >
            Django
          </a>
        </app-cells>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card info-cards__card--nominh">
          <div class="info-cards__row info-cards__row--vertical-center">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Group name:</span>
              <a
                v-if="is_general_manager"
                :href="`https://story.altezza.travel/clients/${form.lead?.final_itinerary?.itinerary_id}`"
                target="_blank"
                class="table-default__activity table-default__activity--itinerary"
                :class="{ 'table-default__activity--active': form.lead?.final_itinerary?.itinerary_id }"
              />
              <router-link
                v-if="form.lead"
                :to="{ name: 'leads-detail-reservations', params: { id: form.lead.id } }"
                class="info-cards__link"
              >
                {{ form.lead.name ? form.lead.name : `#${ form.lead?.id }` }}
              </router-link>
            </div>
          </div>
          <div v-if="form.hotel_management_reservation" class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <router-link
                :to="{ name: 'hotel-management-reservations-detail-reservation', params: { id: form.hotel_management_reservation } }"
                class="info-cards__link"
              >
                PMS: #{{ form.hotel_management_reservation }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
      <app-cells v-if="is_general_manager && form.hotel?.property && !form.hotel_management_reservation">
        <app-button @click.native="onExportPMSClick">
          Export to PMS
        </app-button>
      </app-cells>
      <app-info-text-panel v-if="form.already_booked && form.reservation_status.code !== 'booked'">
        Sales manager marked this reservation as already booked.
      </app-info-text-panel>

      <app-page-container extra>
        <!-- ^TOP^ -- Cancellation actions ---------------------->
        <template v-if="form.cancellation_request">
          <app-cells>
            <h3 v-if="is_reservations_department" class="title">What did the hotel answer regarding the
              cancellation?</h3>
            <h3 v-else class="title">Cancellation actions</h3>
            <app-button
              @click.native="onOpenSendEmailPopup('cancellation')"
              size="small"
            >
              Resend email
            </app-button>
            Reservation status:
            <app-option-picker
              @click="onCancellationOptionSelected($event, true)"
              v-model="form.cancellation_request.reservation_status"
              :options="form.cancellation_request.available_reservation_statuses"
              :disable="form.cancellation_request.available_reservation_statuses < 1 || (!is_general_manager && !is_reservations_department)"
              :amount="form.cancellation_request.amount || null"
            />
            Sales status:
            <app-option-picker
              @click="onCancellationOptionSelected($event, false)"
              v-model="form.cancellation_request.sales_status"
              :options="form.cancellation_request.available_sales_statuses"
              :disable="form.cancellation_request.available_sales_statuses < 1 || (!is_general_manager && !is_reservations_department)"
            />
          </app-cells>
          <app-colored-notes
            v-if="is_reservations_department && form.cancellation_request.notes"
            color="#77b017"
            :text="form.cancellation_request.notes"
          />
          <app-form-group
            v-if="!is_reservations_department"
            label="Sales manager notes for hotel cancellation"
            label-for="notes"
          >
            <app-textarea
              @change.native="onPatchReservationCancellationNotes"
              v-model="form.cancellation_request.notes"
              rows="5"
              placeholder="Write a note"
              id="notes"
              :readonly="!is_general_manager && !is_sales_manager && !is_sales_supervisor"
            />
          </app-form-group>
        </template>
        <!--------------------- BOTTOM> /// Cancellation actions /// -->

        <!-- ^TOP^ -- Amendment actions ---------------------->
        <template v-if="form.amendment_request">
          <app-cells>
            <h3 v-if="is_reservations_department" class="title">What did the hotel answer regarding the amendment?</h3>
            <h3 v-else class="title">Amendment actions</h3>
            <app-button
              v-if="is_general_manager || is_reservations_department"
              @click.native="onOpenSendEmailPopup('amendment')"
              size="small"
            >
              Resend email
            </app-button>
            Reservation status:
            <app-option-picker
              @click="onAmendmentOptionSelected($event, true)"
              v-model="form.amendment_request.reservation_status"
              :options="form.amendment_request.available_reservation_statuses"
              :disable="form.amendment_request.available_reservation_statuses < 1 || (!is_general_manager && !is_reservations_department)"
              :amount="form.amendment_request.amount || null"
            />
            Sales status:
            <app-option-picker
              @click="onAmendmentOptionSelected($event, false)"
              v-model="form.amendment_request.sales_status"
              :options="form.amendment_request.available_sales_statuses"
              :disable="form.amendment_request.available_sales_statuses < 1 || (!is_general_manager && !is_reservations_department)"
            />
          </app-cells>
          <app-colored-notes
            v-if="is_reservations_department && form.amendment_request.notes"
            color="#77b017"
            :text="form.amendment_request.notes"
          />
          <app-form-group
            v-if="!is_reservations_department"
            label="Sales manager notes for hotel amendment"
            label-for="notes"
          >
            <app-textarea
              @change.native="onPatchReservationAmendmentNotes"
              v-model="form.amendment_request.notes"
              rows="5"
              placeholder="Write a note"
              id="notes"
              :readonly="!is_general_manager && !is_sales_manager && !is_sales_supervisor"
            />
          </app-form-group>
        </template>
        <!--------------------- BOTTOM> /// Amendment actions /// -->

        <template v-if="!form.cancellation_request && !form.amendment_request">
          <app-colored-notes v-if="(is_general_manager || is_reservations_department) && form.notes" color="#77b017"
                             :text="form.notes"/>
          <app-form-group
            v-if="!is_reservations_department"
            label="Sales manager notes for the hotel"
            label-for="notes"
            class="form-group--no-indent"
          >
            <app-textarea
              @change.native="onPatchReservationNotes"
              v-model="form.notes"
              rows="5"
              placeholder="Write a note"
              id="notes"
              :readonly="(!is_general_manager && !is_sales_manager && !is_sales_supervisor) && form.reservation_status.code === 'booked'"
            />
          </app-form-group>
        </template>
        <app-cells
          v-if="
            (is_general_manager || is_sales_manager || is_sales_supervisor) &&
            (form.reservation_status.code === 'booked' || form.reservation_status.code === 'booked_provisionally') &&
            !form.cancellation_request && !form.amendment_request && !form.hotel?.property
          "
        >
          <app-button
            v-if="form.reservation_status.code === 'booked' && !form.meal && form.hotel.invoice_required"
            @click.native="onClickAddMealButton"
          >
            Request Meal
          </app-button>
          <app-button @click.native="onShowRequestAmendmentsModal" theme="warn">Amendment</app-button>
          <app-button @click.native="$modal.show('accept-request-modal')" theme="error">Cancellation</app-button>
        </app-cells>
        <app-cells v-if="(is_general_manager || is_sales_manager || is_sales_supervisor) && (form.reservation_status.code === 'booked' ) && form.hotel_management_reservation && !form.rooming_list">
          <app-button @click.native="onShowRequestAmendmentsModal" theme="warn">Amendment</app-button>
          <app-button @click.native="$modal.show('accept-request-property-modal')" theme="error">Cancellation</app-button>
        </app-cells>
        <form @submit.prevent="onCheckForm" style="margin-top: 20px; margin-bottom: 20px;">
          <app-grid v-if="form.amendment_request">
            <template #item-1>
              <h3 class="title title--mb15" style="color: #008000">The new requirements for the booking</h3>
              <h3 class="title title--mb15">Rooms</h3>
              <AppHotelCard
                v-for="(room, index) in form.amendment_request.rooms_short"
                :key="`amendment_rooms_short_${room.id}`"
                :order_number="index + 1"
                :name="room.hotel_room.name"
                :accomodation_type="room.accomodation_type"
                :check_in="room.check_in"
                :check_out="room.check_out"
                :mp="room.meal_plan.name"
                :early="room.early_check_in_time || null"
                :late="room.late_check_out_time || null"
                :count="room.count"
                :clearable="false"
              />
              <template v-if="form.amendment_request.meals?.length">
                <h3 class="title title--mb15">Meals</h3>
                <AppMealCard
                  v-for="(card, index) in form.amendment_request.meals"
                  :key="`meal_card_amendment_${card.id}`"
                  :order-number="index + 1"
                  :meal="card"
                  :is-deletable="false"
                />
              </template>
            </template>
            <template #item-2>
              <h3 class="title title--mb15" style="color: #993300">The details of the original booking</h3>
              <h3 class="title title--mb15">Rooms</h3>
              <AppHotelCard
                v-for="(room, index) in form.rooms_short"
                :key="`rooms_short_${room.id}`"
                :order_number="index + 1"
                :name="room.hotel_room.name"
                :accomodation_type="room.accomodation_type"
                :check_in="room.check_in"
                :check_out="room.check_out"
                :mp="room.meal_plan.name"
                :early="room.early_check_in_time || null"
                :late="room.late_check_out_time || null"
                :count="room.count"
                :clearable="false"
              />
              <template v-if="form.meal && form.meal.requests?.length">
                <h3 class="title title--mb15">Meals</h3>
                <AppMealCard
                  v-for="(card, index) in form.meal.requests"
                  :key="`meal_card_${card.id}`"
                  :order-number="index + 1"
                  :meal="card"
                  :is-deletable="false"
                  is-clients-hide
                />
              </template>
            </template>
          </app-grid>
          <template v-if="!form.amendment_request">
            <app-cells v-if="form?.lead?.client_group">
              <app-filters
                v-model="rooms_filter"
                :filters="rooms_filters"
                name="filterRooms"
              />
              <div
                v-if="(is_general_manager || is_reservations_department) && !form.rooming_list && form.reservation_status.code === 'booked'"
                class="status-title"
                :style="{ 'backgroundColor': '#439ad1' }"
              >
                Missing rooming list
              </div>
            </app-cells>
            <template v-if="rooms_filter === 'rooms_short'">
              <template v-if="form.meal">
                <app-cells :indent="false" :margin-bottom="15">
                  <h3 class="title">Meals</h3>
                  <app-cells :indent="false" gap="8px">
                    Reservation status:
                    <app-option-picker
                      @click="onMealOptionSelected($event)"
                      v-model="form.meal.reservation_status"
                      :options="form.meal.reservation_status.available_reservation_statuses_for_meal"
                      :disable="form.meal.reservation_status.available_reservation_statuses_for_meal < 1 || (!is_general_manager && !is_reservations_department)"
                    />
                    <span v-if="form.meal && form.meal.invoice_amount">
                      <template v-if="form.meal.invoice_amount !== '0.00'">
                        Price: {{ form.meal.invoice_amount.toLocaleString() }}$
                      </template>
                      <template v-else>Price: FOC</template>
                    </span>
                  </app-cells>
                  <button @click="onClickEditMealPrice" v-if="form.meal.reservation_status.code === 'booked'"
                          type="button">
                    <img src="@/assets/img/edit-blue-icon.svg" alt="">
                  </button>
                </app-cells>
                <app-cells v-if="form.meal.already_booked && form.meal.reservation_status.code === 'new'"
                           :indent="false" :margin-bottom="15">
                  <app-colored-notes
                    color="#35a4ea"
                    text="Sales manager marked it as already booked. Double check the order and confirm"
                    :indent="false"
                  />
                </app-cells>
                <app-cells-grid :columns="1" gap="0">
                  <AppMealCard
                    v-for="(card, index) in form.meal.requests"
                    :key="`meal_card_${index}`"
                    :order-number="index + 1"
                    :meal="card"
                    :is-deletable="false"
                  />
                </app-cells-grid>
              </template>
              <app-flex-grid
                v-if="is_general_manager && !form.rooming_list"
                :rows="[
                    ['140px', '140px', '60px', '160px', 1],
                    ['60px', '180px', '60px', '180px', 1],
                    [1]
                  ]"
              >
                <template #header-1>
                  <h3 class="title title--mb15">Room configuration</h3>
                </template>
                <template #col-1-1>
                  <app-form-group label="Check-in" class="form-group--no-indent">
                    <app-date-picker
                      @input="$refs.check_out_form.$children[0].focus()"
                      v-model="form.check_in"
                      placeholder="Check-in"
                      :clearable="false"
                    />
                  </app-form-group>
                </template>
                <template #col-1-2>
                  <app-form-group label="Check-out" class="form-group--no-indent">
                    <app-date-picker
                      v-model="form.check_out"
                      placeholder="Check-out"
                      :readonly="!form.check_in"
                      :default-value="form.check_in"
                      ref="check_out_form"
                      :clearable="false"
                    />
                  </app-form-group>
                </template>
                <template #col-1-3>
                  <app-form-group label="Nights" class="form-group--no-indent" disabled>
                    <app-input :value="calcNumberOfNights" disabled/>
                  </app-form-group>
                </template>
                <template #col-1-4>
                  <app-form-group label="Meal plan" class="form-group--no-indent">
                    <app-select
                      v-model="form.meal_plan_second"
                      :options="meal_plans_options"
                      :clearable="false"
                      :readonly="!form.hotel"
                      label="name"
                      placeholder="Meal plan"
                    />
                  </app-form-group>
                </template>
                <template #col-1-5>
                  <app-form-group label="Room" class="form-group--no-indent">
                    <app-select
                      v-model="form.type_of_rooms"
                      :options="rooms_options"
                      :clearable="false"
                      :readonly="!form.meal_plan_second"
                      label="name"
                      placeholder="Choose rooms"
                    />
                  </app-form-group>
                </template>
                <template #col-2-1>
                  <app-checkbox v-model="form.early_check_in" label="Early"/>
                </template>
                <template #col-2-2>
                  <app-form-group
                    label="Check-in time"
                    label-for="early_check_in_time_form"
                    class="form-group--no-indent"
                    label_left
                    :disabled="!form.early_check_in"
                  >
                    <app-input
                      v-model="form.early_check_in_time"
                      v-mask="'99:99'"
                      placeholder="--:--"
                      autocomplete="off"
                      id="early_check_in_time_form"
                      name="early_check_in_time_form"
                      :disabled="!form.early_check_in"
                      class="input--65"
                    />
                  </app-form-group>
                </template>
                <template #col-2-3>
                  <app-checkbox v-model="form.late_check_out" label="Late"/>
                </template>
                <template #col-2-4>
                  <app-form-group
                    label="Check-out time"
                    label-for="late_check_out_time_form"
                    class="form-group--no-indent"
                    label_left
                    :disabled="!form.late_check_out"
                  >
                    <app-input
                      v-model="form.late_check_out_time"
                      v-mask="'99:99'"
                      placeholder="--:--"
                      autocomplete="off"
                      id="late_check_out_time_form"
                      name="late_check_out_time_form"
                      :disabled="!form.late_check_out"
                      class="input--65"
                    />
                  </app-form-group>
                </template>
                <template #col-2-5>
                  <app-cells :indent="false" position="end">
                    <app-button type="submit" ref="submit_update">
                      Save rooms
                    </app-button>
                  </app-cells>
                </template>
                <template #col-3-1>
                  <app-cells
                    v-if="form.type_of_rooms && form.type_of_rooms.accomodation_types && form.type_of_rooms.accomodation_types.length"
                    :indent="false"
                    gap="10px"
                  >
                    <app-room-icon
                      @click="onRoomIconClick(type)"
                      v-for="(type, index) in form.type_of_rooms.accomodation_types"
                      :key="index"
                      :accomodation_type="type"
                      :disabled="checkExistsInArray(form.rooms_short, convertToRoom(type))"
                      :class="{ 'room-icon__disabled': checkExistsInArray(form.rooms_short, convertToRoom(type)) }"
                    />
                  </app-cells>
                </template>
              </app-flex-grid>
              <h3 class="title title--mb15">Rooms</h3>
              <AppHotelCard
                v-for="(room, index) in form.rooms_short"
                :key="`rooms_short_${room.id}`"
                @onCardDelete="onShortRoomDelete(index, 'rooms_short')"
                @onCountInput="onCountChange($event, index, 'rooms_short')"
                :order_number="index + 1"
                :name="room.hotel_room.name"
                :accomodation_type="room.accomodation_type"
                :check_in="room.check_in"
                :check_out="room.check_out"
                :mp="room.meal_plan.name"
                :early="room.early_check_in_time || null"
                :late="room.late_check_out_time || null"
                :count="room.count"
                :clearable="is_general_manager && !form.rooming_list"
              />
            </template>
            <template v-if="rooms_filter === 'rooms_list'">
              <h3 class="title title--mb15">Rooms</h3>
              <app-cells-grid :columns="1" gap="0">
                <AppHotelCard
                  v-for="(room, index) in form.reservation_rooms"
                  :key="`reservation_rooms_${room.id}`"
                  @onChangeRoomClients="onChangeRoomClients($event, index)"
                  @onCardDelete="onRoomDelete(index)"
                  :order_number="index + 1"
                  :name="room.hotel_room.name"
                  :accomodation_type="room.accomodation_type"
                  :check_in="room.check_in"
                  :check_out="room.check_out"
                  :mp="room.meal_plan.name"
                  :early="room.early_check_in_time || null"
                  :late="room.late_check_out_time || null"
                  :rooming_list="form.rooming_list"
                  :clients_selected="room.clients"
                  :clients_options="clients_options"
                  :clearable="false"
                  :short_name="room.hotel_management_room?.room.short_name"
                />
              </app-cells-grid>
              <template v-if="form.meal && form.meal.reservation_status?.code !== 'cancelled'">
                <h3 class="title title--mb15">Meals</h3>
                <app-cells-grid :columns="1" gap="0">
                  <AppMealCard
                    v-for="(card, index) in form.meal.requests"
                    :key="`meal_card_${index}`"
                    @changeMealClients="onChangeMealClients($event, index)"
                    :meal="card"
                    :order-number="index + 1"
                    :clients-options="clients_options"
                    :is-select-visible="!form.rooming_list"
                    :is-deletable="false"
                  />
                </app-cells-grid>
              </template>
              <div v-if="form.sales_status.code === 'book' && !form.amendment_request" class="downloads">
                <div class="downloads__item" :class="{'downloads__item--loading': payment_completed_loader}">
                  <div class="downloads__left">
                    <div class="downloads__info">
                      <span class="downloads__name downloads__name--no-events">Rooming list completed</span>
                    </div>
                  </div>
                  <div class="downloads__right">
                    <div class="downloads__values">
                      <span class="downloads__key">Status:</span>
                      <app-switcher
                        @change="onUpdatePaymentData"
                        v-model="form.rooming_list"
                        :readonly="(!is_general_manager && !is_sales_manager && !is_sales_supervisor) && !form.lead?.client_group || form.guest_details"
                      />
                    </div>
                    <template v-if="form.rooming_list && form.rooming_list_by && !payment_completed_loader">
                      <div class="downloads__values">
                        <span class="downloads__key">Completed at:</span>
                        <span class="downloads__value downloads__width">
                          {{ dateTimeFormat(form.rooming_list_at) }}</span>
                      </div>
                      <div class="downloads__values">
                        <span class="downloads__key">Completed by:</span>
                        <div class="downloads__person downloads__width">
                          <div class="downloads__photo">
                            <img :src="getUserAvatar(form.rooming_list_by?.crop.crop_url)" alt="Person">
                          </div>
                          <span class="downloads__value">{{ form.rooming_list_by?.full_name || '-' }}</span>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </template>
          </template>
        </form>
      </app-page-container>

      <app-tabs :tabs="tabsToShow"/>
      <transition name="fade" mode="out-in">
        <router-view
          @get-reservation-data="getReservationData"
          :reservation="form"
          :rooms_options="rooms_options"
          ref="childComponent"
        />
      </transition>
    </template>
    <modal
      name="accept-request-property-modal"
      :width="500"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('accept-request-property-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Are you sure you want to cancel?</div>
        <div class="popup__body">
          <app-cells position="center" :indent="false">
            <app-button @click.native="onRequestCancellationProperty" ref="request">Request</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="accept-request-modal"
      :width="500"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('accept-request-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Request full cancellation</div>
        <div class="popup__body">
          <div v-if="form.cancellation_rate" class="popup__subt">
            Cancellation fee <u>{{ form.cancellation_rate }}</u>% will apply for this reservation.<br> This request will
            be sent to the reservations department to process the cancellation manually.<br> You can provide additional
            notes for the hotel below.
          </div>
          <div v-else class="popup__subt">
            Cancellation fees will not apply for this reservation, and we will automatically send an email to the
            airline. You can provide additional notes below.
          </div>
          <app-form-group
            label="Sales manager notes for hotel cancellation"
            label-for="notes"
          >
            <app-textarea
              v-model="notes_cancellation"
              rows="5"
              placeholder="Write a note"
              id="notes"
            />
          </app-form-group>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onRequestCancellation" ref="request">Request</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="manager"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('manager')" class="popup__close" type="button"></button>
        <div class="popup__title">Assign reservation manager</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckManagerForm">
            <app-form-group label="Reservation manager" required>
              <app-select
                v-model="manager.reservation_manager"
                :reduce="item => item.id"
                :options="managers_options"
                :clearable="false"
                :display-field="option => option.full_name"
                label="full_name"
                placeholder="Choose manager"
                :error="$v.manager.reservation_manager.$error"
              />
              <template #error>
                <div v-if="$v.manager.reservation_manager.$dirty && !$v.manager.reservation_manager.required">Required
                  field
                </div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.manager.$error" type="submit" ref="submit_manager">Assign manager</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="deadline"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div v-if="reservation_status_data" class="popup">
        <button @click="onHideDeadlinePopup" class="popup__close" type="button"></button>
        <div class="popup__title">
          {{ reservation_status_data.title }}
        </div>
        <div class="popup__body">
          <app-form-group v-if="reservation_status_data.reservation_status === 'booked_provisionally'" label="Deadline"
                          required>
            <app-date-picker
              v-model="reservation_status_data.reservation_deadline"
              placeholder="Deadline"
            />
          </app-form-group>
          <app-form-group label="Additional message to Sales" label-for="notes">
            <app-textarea
              v-model="reservation_status_data.additional_message"
              rows="5"
              placeholder="Write a message"
              id="notes"
            />
          </app-form-group>
          <app-cells position="center" :indent="false">
            <app-button
              @click.native="onPatchDeadline"
              ref="submit_deadline"
            >
              Save
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="request-amendment"
      :width="950"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('request-amendment')" class="popup__close" type="button"></button>
        <div class="popup__title">Request amendment</div>
        <div class="popup__body">
          <form @submit.prevent="onSendRequestAmendmentForm">
            <app-flex-grid
              :rows="[
                ['140px', '140px', '60px', '160px', 1],
                ['60px', '180px', '60px', '180px'],
                [1]
              ]"
            >
              <template #header-1>
                <h3 class="title title--mb15">Room configuration</h3>
              </template>
              <template #col-1-1>
                <app-form-group label="Check-in" class="form-group--no-indent">
                  <app-date-picker
                    @input="$refs.check_out_request_amendments.$children[0].focus()"
                    v-model="form_request_amendments.check_in"
                    placeholder="Check-in"
                  />
                </app-form-group>
              </template>
              <template #col-1-2>
                <app-form-group label="Check-out" class="form-group--no-indent">
                  <app-date-picker
                    v-model="form_request_amendments.check_out"
                    placeholder="Check-out"
                    :readonly="!form_request_amendments.check_in"
                    :default-value="form_request_amendments.check_in"
                    ref="check_out_request_amendments"
                  />
                </app-form-group>
              </template>
              <template #col-1-3>
                <app-form-group label="Nights" class="form-group--no-indent" disabled>
                  <app-input :value="calcNumberOfNightsOnRequestAmendments" placeholder="0" disabled/>
                </app-form-group>
              </template>
              <template #col-1-4>
                <app-form-group label="Meal plan" class="form-group--no-indent">
                  <app-select
                    v-model="form_request_amendments.meal_plan"
                    :options="meal_plans_options"
                    :clearable="false"
                    label="name"
                    placeholder="Meal plan"
                    :readonly="!form_request_amendments.check_out"
                  />
                </app-form-group>
              </template>
              <template #col-1-5>
                <app-form-group label="Room" class="form-group--no-indent">
                  <app-select
                    v-model="form_request_amendments.type_of_rooms"
                    :options="rooms_options"
                    :clearable="false"
                    label="name"
                    placeholder="Choose rooms"
                    :readonly="!form_request_amendments.meal_plan"
                  />
                </app-form-group>
              </template>
              <template #col-2-1>
                <app-checkbox v-if="!form.hotel?.property" v-model="form_request_amendments.early_check_in" label="Early"/>
              </template>
              <template #col-2-2>
                <app-form-group
                  v-if="!form.hotel?.property"
                  label="Check-in time"
                  label-for="early_check_in_time"
                  class="form-group--no-indent"
                  label_left
                  :disabled="!form_request_amendments.early_check_in"
                >
                  <app-input
                    v-model="form_request_amendments.early_check_in_time"
                    v-mask="'99:99'"
                    placeholder="--:--"
                    autocomplete="off"
                    id="early_check_in_time"
                    name="early_check_in_time"
                    @paste.native.prevent
                    :disabled="!form_request_amendments.early_check_in"
                    class="input--65"
                  />
                </app-form-group>
              </template>
              <template #col-2-3>
                <app-checkbox v-if="!form.hotel?.property" v-model="form_request_amendments.late_check_out" label="Late"/>
              </template>
              <template #col-2-4>
                <app-form-group
                  v-if="!form.hotel?.property"
                  label="Check-out time"
                  label-for="late_check_out_time"
                  class="form-group--no-indent"
                  label_left
                  :disabled="!form_request_amendments.late_check_out"
                >
                  <app-input
                    v-model="form_request_amendments.late_check_out_time"
                    v-mask="'99:99'"
                    placeholder="--:--"
                    autocomplete="off"
                    id="late_check_out_time"
                    name="late_check_out_time"
                    @paste.native.prevent
                    :disabled="!form_request_amendments.late_check_out"
                    class="input--65"
                  />
                </app-form-group>
              </template>
              <template #col-3-1>
                <app-cells
                  v-if="form_request_amendments.type_of_rooms && form_request_amendments.type_of_rooms.accomodation_types && form_request_amendments.type_of_rooms.accomodation_types.length"
                  :indent="false"
                  gap="10px"
                >
                  <app-room-icon
                    @click="onAmendmentsRoomIconClick(type)"
                    v-for="(type, index) in form_request_amendments.type_of_rooms.accomodation_types"
                    :key="index"
                    :accomodation_type="type"
                    :disabled="checkExistsInArrayAmendment(form_request_amendments.reservation_rooms, convertToRoomAmendment(type))"
                    :class="{ 'room-icon__disabled': checkExistsInArrayAmendment(form_request_amendments.reservation_rooms, convertToRoomAmendment(type)) }"
                  />
                </app-cells>
              </template>
            </app-flex-grid>
            <h3 class="title title--mb15">Rooms</h3>
            <app-cells-grid
              v-if="form_request_amendments.reservation_rooms.length"
              :columns="1"
              gap="10px"
              :margin-bottom="30"
            >
              <app-rooms-edit
                v-for="(room, index) in form_request_amendments.reservation_rooms"
                :key="room.id || room.__key"
                @cardCancel="onCardCancel(index)"
                @cardRestore="onCardRestore(index)"
                @cardDelete="onCardDelete(index)"
                :count="index + 1"
                :mp_options="meal_plans_options"
                :room="room"
                :property="form.hotel?.property"
              />
            </app-cells-grid>
            <app-no-results v-else text="There’s no rooms yet"/>

            <template v-if="form.meal && form.meal.requests.length">
              <app-flex-grid
                :rows="[[1, '130px', '80px', '80px', '80px', 'auto']]"
                align-items="flex-end"
                margin-top="25px"
              >
                <template #header-1>
                  <h3 class="title title--mb15">Meals configuration</h3>
                </template>
                <template #col-1-1>
                  <app-form-group label="Type" class="form-group--no-indent">
                    <app-select
                      v-model="form_request_amendments.meal_type"
                      :options="meal_type_options"
                      :clearable="false"
                      label="name"
                      placeholder="Choose type"
                    />
                  </app-form-group>
                </template>
                <template #col-1-2>
                  <app-form-group label="Date" class="form-group--no-indent">
                    <app-date-picker
                      v-model="form_request_amendments.meal_date"
                      placeholder="Date"
                      :clearable="false"
                    />
                  </app-form-group>
                </template>
                <template #col-1-3>
                  <app-form-group label="Time" class="form-group--no-indent">
                    <app-input
                      v-model="form_request_amendments.meal_time"
                      v-mask="'99:99'"
                      placeholder="00:00"
                      autocomplete="off"
                      id="time"
                      name="time"
                      @paste.native.prevent
                    />
                  </app-form-group>
                </template>
                <template #col-1-4>
                  <app-form-group label="Clients" label-for="add_meal_clients" class="form-group--no-indent">
                    <app-input
                      v-model.number="form_request_amendments.meal_clients"
                      type="number"
                      name="add_meal_clients"
                      id="add_meal_clients"
                      placeholder=""
                    />
                  </app-form-group>
                </template>
                <template #col-1-5>
                  <app-form-group label="Crew" label-for="add_meal_crew" class="form-group--no-indent">
                    <app-input
                      v-model.number="form_request_amendments.meal_crew"
                      type="number"
                      name="add_meal_crew"
                      id="add_meal_crew"
                      placeholder=""
                    />
                  </app-form-group>
                </template>
                <template #col-1-6>
                  <app-button
                    @click.native="onClickAmendmentAddMealConfig"
                    :disabled="!form_request_amendments.meal_type || !form_request_amendments.meal_time || !form_request_amendments.meal_clients || !form_request_amendments.meal_crew"
                  >
                    Add
                  </app-button>
                </template>
              </app-flex-grid>
              <template v-if="form_request_amendments.meal_requests.length">
                <h3 class="title title--mb15">Meals</h3>
                <app-cells-grid :columns="1" gap="0">
                  <AppMealCard
                    v-for="(card, index) in form_request_amendments.meal_requests"
                    :key="card.id || card.__key"
                    @changeFormElement="(value, element) => onChangeAmendmentMealFormElement(index, value, element)"
                    @cardCancel="onCardAmendmentCancel(index)"
                    @cardRestore="onCardAmendmentRestore(index)"
                    @cardDelete="onCardAmendmentDelete(index)"
                    :meal="card"
                    :order-number="index + 1"
                    is-deletable
                    is-editable
                    is-status-mode
                    is-clients-hide
                  />
                </app-cells-grid>
              </template>
            </template>

            <app-form-group v-if="!form.hotel?.property" label="Sales manager notes for hotel amendment" label-for="notes_request_amendments">
              <app-textarea
                v-model="form_request_amendments.notes"
                rows="5"
                placeholder="Write a note"
                id="notes_request_amendments"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="request_amendment_submit">
                Request
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="send-email-popup"
      :width="450"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('send-email-popup')" class="popup__close" type="button"></button>
        <div class="popup__title">Send {{ current_action }} email</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckSendEmailForm">
            <app-form-group label="Communication email">
              <template #additional>
                <img
                  v-if="form?.hotel?.emails_reservation.length > 1"
                  src="@/assets/img/warning-icon.svg"
                  alt="Confirmed"
                  v-tooltip="{ content: 'If the communication email is not chosen, we will send emails to all hotel addresses', placement: 'top', offset: 10 }"
                >
              </template>
              <app-select
                v-model="form_send_email.communication_email"
                :reduce="item => item.id"
                :options="form?.hotel?.emails_reservation"
                :readonly="form?.hotel?.emails_reservation.length === 1"
                :display-field="option => option.email"
                label="email"
                placeholder="Choose email"
              />
            </app-form-group>
            <app-form-group label="Template" required>
              <app-select
                v-model="form_send_email.email_template"
                :reduce="item => item.id"
                :options="email_templates"
                :clearable="false"
                :get-option-label="item => item.sendpulse_template.name"
                :display-field="option => option.sendpulse_template.name"
                placeholder="Choose template"
                :error="$v.form_send_email.email_template.$error"
              />
              <template #error>
                <div v-if="$v.form_send_email.email_template.$dirty && !$v.form_send_email.email_template.required">
                  Required field
                </div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="send_email_submit" :disabled="$v.form_send_email.$error">
                Send
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="accept-delete-res"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('accept-delete-res')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete hotel reservation
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this reservation?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('accept-delete-res')" theme="dark">Cancel</app-button>
            <app-button @click.native="onHotelResDelete" ref="delete_res">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="cancellation-reservation-accept"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onCloseAcceptCancellationReservation" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Change status
        </div>
        <div class="popup__body">
          <div class="popup__subt">
            Are you sure you want to change status to {{ current_cancellation_status.name?.toUpperCase() }}?
          </div>
          <app-form-group
            v-if="current_cancellation_status && current_cancellation_status.amount_required"
            label="Amount (USD)"
            label-for="cancellation_amount"
            required
          >
            <app-fin-number
              v-model="cancellation_amount"
              id="cancellation_amount"
              placeholder="0"
              name="cancellation_amount"
              :error="$v.cancellation_amount.$error"
            />
            <template #error>
              <div v-if="$v.cancellation_amount.$dirty && !$v.cancellation_amount.required">Required field</div>
            </template>
          </app-form-group>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onCloseAcceptCancellationReservation" theme="dark">Cancel</app-button>
            <app-button
              @click.native="onCheckCancellationReservationStatusForm"
              ref="cancellation_reservation_status"
              :disabled="$v.cancellation_amount.$error"
            >
              Yes
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="amendment-reservation-accept"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onCloseAcceptAmendmentReservation" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Change status
        </div>
        <div class="popup__body">
          <div class="popup__subt">
            Are you sure you want to change status to {{ current_amendment_status.name?.toUpperCase() }}?
          </div>
          <app-form-group
            v-if="current_amendment_status && current_amendment_status.amount_required"
            label="Amount (USD)"
            label-for="cancellation_amount"
            required
          >
            <app-fin-number
              v-model="amendment_amount"
              id="cancellation_amount"
              placeholder="0"
              name="cancellation_amount"
              :error="$v.amendment_amount.$error"
            />
            <template #error>
              <div v-if="$v.amendment_amount.$dirty && !$v.amendment_amount.required">Required field</div>
            </template>
          </app-form-group>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onCloseAcceptAmendmentReservation" theme="dark">Cancel</app-button>
            <app-button
              @click.native="onCheckAmendmentReservationStatusForm"
              ref="amendment_reservation_status"
              :disabled="$v.amendment_amount.$error"
            >
              Yes
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="request-meal-popup"
      :width="920"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('request-meal-popup')" class="popup__close" type="button"></button>
        <div class="popup__title">
          <app-cells :indent="false" position="center">
            Request Meal
            <app-checkbox
              v-model="form_request_meal.already_booked"
              label="Already booked"
            />
          </app-cells>
        </div>
        <div class="popup__body">
          <form @submit.prevent="sendRequestMealForm">
            <app-flex-grid :rows="[['150px', '100px'], [1, '130px', '80px', '80px', '80px', 'auto']]"
                           align-items="flex-end">
              <template #col-1-1 v-if="is_general_manager">
                <app-form-field label="Already booked">
                  <app-switcher v-model="form_request_meal.already_booked_general"/>
                </app-form-field>
              </template>
              <template #col-1-2 v-if="is_general_manager && form_request_meal.already_booked_general">
                <app-form-field label="Price" label-for="price_amount" required>
                  <app-input
                    v-model.number="form_request_meal.invoice_amount"
                    type="number"
                    id="price_amount"
                    name="price_amount"
                    placeholder="0"
                    :error="$v.form_request_meal.invoice_amount.$error"
                  />
                  <template #error>
                    <div
                      v-if="$v.form_request_meal.invoice_amount.$dirty && !$v.form_request_meal.invoice_amount.required">
                      Required field
                    </div>
                  </template>
                </app-form-field>
              </template>
              <template #header-2>
                <h3 class="title title--mb15">Configuration</h3>
              </template>
              <template #col-2-1>
                <app-form-group label="Type" class="form-group--no-indent" required>
                  <app-select
                    v-model="form_request_meal.type"
                    :options="meal_type_options"
                    :clearable="false"
                    label="name"
                    placeholder="Choose type"
                    :error="$v.form_request_meal.type.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.type.$dirty && !$v.form_request_meal.type.required">Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-2>
                <app-form-group label="Date" class="form-group--no-indent" required>
                  <app-date-picker
                    v-model="form_request_meal.date"
                    placeholder="Date"
                    :error="$v.form_request_meal.date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.date.$dirty && !$v.form_request_meal.date.required">
                      Required field
                    </div>
                    <div v-if="$v.form_request_meal.date.$dirty && !$v.form_request_meal.date.minValue">
                      Date cannot be less than the current date
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-3>
                <app-form-group label="Time" class="form-group--no-indent" required>
                  <app-input
                    v-model="form_request_meal.time"
                    v-mask="'99:99'"
                    placeholder="00:00"
                    autocomplete="off"
                    id="time"
                    name="time"
                    @paste.native.prevent
                    :error="$v.form_request_meal.time.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.time.$dirty && !$v.form_request_meal.time.timeRegex">Wrong time
                    </div>
                    <div v-if="$v.form_request_meal.time.$dirty && !$v.form_request_meal.time.required">Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-4>
                <app-form-group label="Clients" label-for="add_meal_clients" class="form-group--no-indent" required>
                  <app-input
                    v-model.number="form_request_meal.clients"
                    type="number"
                    name="add_meal_clients"
                    id="add_meal_clients"
                    placeholder=""
                    :error="$v.form_request_meal.clients.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.clients.$dirty && !$v.form_request_meal.clients.required">Required
                      field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-5>
                <app-form-group label="Crew" label-for="add_meal_crew" class="form-group--no-indent" required>
                  <app-input
                    v-model.number="form_request_meal.crew"
                    type="number"
                    name="add_meal_crew"
                    id="add_meal_crew"
                    placeholder=""
                    :error="$v.form_request_meal.crew.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_request_meal.crew.$dirty && !$v.form_request_meal.crew.required">Required field
                    </div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-6>
                <app-button @click.native="onClickRequestMealConfig" :disabled="$v.form_request_meal.$error">Add
                </app-button>
              </template>
            </app-flex-grid>
            <h3 class="title title--mb15">Meals</h3>
            <template v-if="form_request_meal.requests.length">
              <app-cells-grid :columns="1" gap="0" :margin-bottom="10">
                <AppMealCard
                  v-for="(card, index) in form_request_meal.requests"
                  :key="`meal_card_${index}`"
                  @changeMealClients="onChangeMealRequestClients($event, index)"
                  @cardDelete="onDeleteMealsCard(index)"
                  :meal="card"
                  :order-number="index + 1"
                  :clients-options="clients_options"
                  :is-select-visible="clients_options.length > 0"
                />
              </app-cells-grid>
            </template>
            <app-no-results v-else text="There’s no meals yet" :margin-bottom="20"/>
            <app-cells :indent="false" position="center">
              <app-button type="submit" ref="request_meal_submit" :disabled="!form_request_meal.requests.length">
                Request
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="meal-not-available-popup"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="closeMealNotAvailablePopup" class="popup__close" type="button"></button>
        <div class="popup__title">Not available</div>
        <div class="popup__body">
          <app-form-group label="Additional message to Sales" label-for="notes">
            <app-textarea
              v-model="meal_additional_message"
              rows="5"
              placeholder="Write a message"
              id="notes"
            />
          </app-form-group>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onClickSaveMealNotAvailableButton" ref="submit_meal_not_available">
              Save
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="meal-booked-popup"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="closeMealBookedPopup" class="popup__close" type="button"></button>
        <div class="popup__title">
          Booked {{ form.meal?.invoice_amount ? `(${ form.meal.invoice_amount.toLocaleString() }$)` : '(FOC)' }}
        </div>
        <div class="popup__body">
          <app-form-group label="Price" label-for="meal_booked_invoice_amount" required>
            <app-input
              v-model.number="form_meal_booked.invoice_amount"
              type="number"
              id="meal_booked_invoice_amount"
              name="meal_booked_invoice_amount"
              placeholder="0"
              :error="$v.form_meal_booked.invoice_amount.$error"
            />
            <template #error>
              <div v-if="$v.form_meal_booked.invoice_amount.$dirty && !$v.form_meal_booked.invoice_amount.required">
                Required field
              </div>
            </template>
          </app-form-group>
          <app-form-group
            v-if="form.supplier_invoice"
            label="Invoice amount"
            label-for="meal_booked_reservation_invoice_amount"
            required
          >
            <app-input
              v-model.number="form_meal_booked.reservation_invoice_amount"
              id="meal_booked_reservation_invoice_amount"
              type="number"
              min="0"
              placeholder="0"
              name="meal_booked_reservation_invoice_amount"
              :error="$v.form_meal_booked.reservation_invoice_amount.$error"
            />
            <template #error>
              <div
                v-if="$v.form_meal_booked.reservation_invoice_amount.$dirty && !$v.form_meal_booked.reservation_invoice_amount.required">
                Required field
              </div>
            </template>
          </app-form-group>
          <app-form-group v-if="form.supplier_invoice" label="Invoice" required>
            <app-uploader
              v-model="form_meal_booked.reservation_invoice"
              class="custom-uploader--min-150"
              :extensions="['application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
            />
            <template #error>
              <div
                v-if="$v.form_meal_booked.reservation_invoice.$dirty && !$v.form_meal_booked.reservation_invoice.required">
                Required field
              </div>
            </template>
          </app-form-group>
          <app-form-group label="Additional message to Sales" label-for="meal_booked_notes">
            <app-textarea
              v-model="form_meal_booked.notes"
              rows="5"
              placeholder="Write a message"
              id="meal_booked_notes"
            />
          </app-form-group>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onClickSaveMealBookedButton" ref="submit_meal_booked"
                        :disabled="$v.form_meal_booked.$error">
              Save
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="meal-price-popup"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('meal-price-popup')" class="popup__close" type="button"></button>
        <div class="popup__title">Meal Price</div>
        <div class="popup__body">
          <app-form-group label="Price" label-for="meal_price_invoice_amount" required>
            <app-fin-number
              v-model.number="form_meal_price.invoice_amount"
              id="meal_price_invoice_amount"
              name="mealprice_invoice_amount"
              placeholder="0"
              :error="$v.form_meal_price.invoice_amount.$error"
            />
            <template #error>
              <div v-if="$v.form_meal_price.invoice_amount.$dirty && !$v.form_meal_price.invoice_amount.required">
                Required field
              </div>
            </template>
          </app-form-group>
          <app-cells :indent="false" position="center">
            <app-button @click.native="onCheckMealPriceForm" ref="meal_price">Save</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="pms-modal"
      :width="600"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('pms-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Export to PMS</div>
        <div class="popup__body">
          <AppHotelCard
            v-for="(room, index) in form.rooms_short"
            :key="`rooms_short_${room.id}`"
            @changeSomeField="onChangeSomeFieldPMS($event, index)"
            :order_number="index + 1"
            :name="room.hotel_room.name"
            :accomodation_type="room.accomodation_type"
            :check_in="room.check_in"
            :check_out="room.check_out"
            :mp="room.meal_plan.name"
            :early="room.early_check_in_time || null"
            :late="room.late_check_out_time || null"
            :count="room.count"
            :clearable="false"
            :bed-in-twin="room.hotel_room.bed_in_twin"
            :property="form.hotel.property"
          />
          <app-cells :indent="false" position="center">
            <app-button @click.native="onSendPMSForm" ref="pms_send">Send</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteReservationsHotel,
  getHotelsRooms,
  getReservationsEmailTemplate,
  getReservationsHotel,
  getReservationsHotelsClients,
  getReservationsMealType,
  getUsers,
  patchLead,
  patchReservationsHotel,
  patchReservationsHotelAmendmentRequest,
  patchReservationsHotelMeal,
  patchReservationsHotelsCancellationRequest,
  patchReservationsHotelsMeal,
  postReservationsHotelAmendmentRequest,
  postReservationsHotelMealRequests,
  postReservationsHotelRooms,
  postReservationsHotelsAmendmentRequestsEmail,
  postReservationsHotelsCancellationRequest,
  postReservationsHotelsCancellationRequestsEmail,
  postReservationsHotelsHotelManagementCancel, postReservationsHotelsHotelManagementCreate,
  postReservationsHotelsMeal,
} from '@/http'
import AppRoomIcon from '@/components/AppRoomIcon.vue'
import { helpers, required, requiredIf } from 'vuelidate/lib/validators'
import AppTabs from '@/components/AppTabs'
import { format, parse, parseISO } from 'date-fns'
import AppInfoTextPanel from '@/components/AppInfoTextPanel.vue'
import AppRoomsEdit from '@/components/AppRoomsEdit.vue'
import AppColoredNotes from '@/components/AppColoredNotes.vue'
import AppOptionPicker from '@/components/AppOptionPicker.vue'
import AppHotelCard from '@/components/AppHotelCard.vue'
import AppMealCard from '@/components/AppMealCard.vue'

const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):([0-5][0-9])?$/i)

export default {
  name: 'HotelsReservationsLayout',
  components: {
    AppMealCard,
    AppHotelCard,
    AppOptionPicker,
    AppInfoTextPanel,
    AppTabs,
    AppRoomIcon,
    AppColoredNotes,
    AppRoomsEdit
  },
  data() {
    return {
      is_loaded: false,
      tabs: [
        { name: 'reservation-hotels-general', label: 'General' },
        { name: 'reservation-hotels-history', label: 'History' },
      ],
      form: {
        reservation_rooms: [],
        rooms_short: [],
        early_check_in_time: '',
        late_check_out_time: '',
        early_check_in: false,
        late_check_out: false
      },
      form_request_amendments: { reservation_rooms: [], meal_requests: [] },
      meal_plans_options: [],
      rooms_options: [],
      placement: 'bottom',
      admin_url: process.env.VUE_APP_ADMIN_URL,
      cancellation_amount: null,
      amendment_amount: null,
      manager: {},
      managers_options: [],
      reservation_status_data: null,
      reservation_deadline: null,
      form_send_email: {},
      email_templates: [],
      current_action: '',
      payment_completed_loader: false,
      current_cancellation_status: {},
      current_amendment_status: {},
      notes_cancellation: '',
      rooms_filter: 'rooms_short',
      rooms_filters: [
        { value: 'rooms_short', label: 'Short summary' },
        { value: 'rooms_list', label: 'Rooming list' },
      ],
      form_request_meal: { date: null, requests: [] },
      meal_type_options: [],
      clients_options: [],
      meal_additional_message: '',
      form_meal_booked: {},
      form_meal_price: {},
      pms_form: {},
      hmr_flag: false,
    }
  },
  validations: {
    form: {},
    manager: {
      reservation_manager: { required }
    },
    form_send_email: {
      email_template: { required },
    },
    cancellation_amount: {
      required: requiredIf(function () {
        return this.current_cancellation_status && this.current_cancellation_status.amount_required
      }),
    },
    amendment_amount: {
      required: requiredIf(function () {
        return this.current_amendment_status && this.current_amendment_status.amount_required
      }),
    },
    form_request_meal: {
      type: { required },
      date: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        },
      },
      time: { required, timeRegex },
      clients: { required },
      crew: { required },
      invoice_amount: {
        required: requiredIf(function () {
          return this.form_request_meal.already_booked_general && this.is_general_manager
        }),
      }
    },
    form_meal_booked: {
      invoice_amount: { required },
      reservation_invoice_amount: {
        required: requiredIf(function () {
          return this.form.supplier_invoice
        }),
      },
      reservation_invoice: {
        required: requiredIf(function () {
          return this.form.supplier_invoice
        }),
      },
    },
    form_meal_price: {
      invoice_amount: { required }
    },
  },
  computed: {
    calcNumberOfNights() {
      if (this.form.check_in && this.form.check_out) {
        const date1 = new Date(this.form.check_in)
        const date2 = new Date(this.form.check_out)
        const timeDiff = date2.getTime() - date1.getTime()
        return Math.ceil(timeDiff / (
          1000 * 3600 * 24
        )) < 0 ? 0 : Math.ceil(timeDiff / (
            1000 * 3600 * 24
          ))
      } else {
        return ''
      }
    },
    calcNumberOfNightsOnRequestAmendments() {
      if (this.form_request_amendments.check_in && this.form_request_amendments.check_out) {
        const date1 = new Date(this.form_request_amendments.check_in)
        const date2 = new Date(this.form_request_amendments.check_out)
        const timeDiff = date2.getTime() - date1.getTime()
        return Math.ceil(timeDiff / (
          1000 * 3600 * 24
        )) < 0 ? 0 : Math.ceil(timeDiff / (
            1000 * 3600 * 24
          ))
      } else {
        return ''
      }
    },
    is_super_user() {
      return this.$store.state.userData.is_superuser
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_reservations_department() {
      return this.$store.state.userGroups.map(item => item.code).includes('reservation_manager')
        || this.$store.state.userGroups.map(item => item.code).includes('reservation_assistant')
        || this.$store.state.userGroups.map(item => item.code).includes('reservation_supervisor')
    },
    tabsToShow() {
      const tabs = []
      this.tabs.forEach(item => {
        if (!item.roles || !item.roles.length) {
          tabs.push(item)
          return
        }
        item.roles.forEach(role => {
          if (this.$store.state.userGroups.some(groupRole => groupRole.code === role)) tabs.push(item)
        })
      })
      return tabs
    },
  },
  watch: {
    form() {
      if (this.form) {
        this.tabs = [
          { name: 'reservation-hotels-general', label: 'General' },
          { name: 'reservation-hotels-history', label: 'History' },
        ]
        if (this.form.hotel.invoice_required && this.form.reservation_status.code === 'booked') {
          this.tabs.splice(1, 0, { name: 'reservation-hotels-finance', label: 'Finance' })
        }
        if (this.form.rooming_list) {
          this.tabs.splice(3, 0, { name: 'reservation-hotels-clients', label: 'Clients' })
        }
        if (this.form.hotel.email_required) {
          this.tabs.splice(this.tabs.length - 1, 0, { name: 'reservation-hotels-emails', label: 'Emails' })
        }
      }
    }
  },
  created() {
    this.runReservationProcess()
    getReservationsMealType().then(res => { this.meal_type_options = res.data })
  },
  methods: {
    onChangeSomeFieldPMS({ value, field }, index) {
      this.form.rooms_short[index][field] = value
    },
    onSendPMSForm() {
      this.$refs.pms_send.preload = true
      postReservationsHotelsHotelManagementCreate(this.$route.params.id, this.normalizePMSForm())
        .finally(() => { this.$refs.pms_send.preload = false })
        .then(res => {
          this.getReservationData()
          this.$notify({
            type: 'success',
            title: 'Change saved',
          })
          this.$modal.hide('pms-modal')
        })
    },
    normalizePMSForm() {
      const form = {}

      form.rooms_short = this.form.rooms_short
        .map(({
          id,
          hotel_room,
          accomodation_type,
          check_in,
          check_out,
          meal_plan,
          early_check_in_time,
          late_check_out_time,
          count,
          bed_in_twin_action,
          bed_in_twin_room_id
        }) => {
          const base = {
            id,
            hotel_room: hotel_room.id,
            accomodation_type: accomodation_type.code,
            check_in,
            check_out,
            meal_plan: meal_plan.code,
            early_check_in_time,
            late_check_out_time,
            count
          }

          return hotel_room?.bed_in_twin ? { ...base, bed_in_twin_room_id, bed_in_twin_action } : base
        })

      return form
    },
    onExportPMSClick() {
      this.$modal.show('pms-modal')
    },
    onCheckMealPriceForm() {
      this.$v.form_meal_price.$touch()
      if (this.$v.form_meal_price.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendMealPriceForm()
      }
    },
    sendMealPriceForm() {
      this.$refs.meal_price.preload = true
      patchReservationsHotelMeal(this.$route.params.id, this.form.meal.id, this.form_meal_price)
        .finally(() => { this.$refs.meal_price.preload = false })
        .then(() => {
          this.getReservationData()
          this.$modal.hide('meal-price-popup')
        })
    },
    onClickEditMealPrice() {
      this.form_meal_price = { invoice_amount: this.form.meal.invoice_amount }
      this.$v.form_meal_price.$reset()
      this.$modal.show('meal-price-popup')
    },
    closeMealNotAvailablePopup() {
      this.is_loaded = false
      this.$modal.hide('meal-not-available-popup')
      this.getReservationData()
    },
    closeMealBookedPopup() {
      this.is_loaded = false
      this.$modal.hide('meal-booked-popup')
      this.getReservationData()
    },
    onClickSaveMealBookedButton() {
      this.$v.form_meal_booked.$touch()
      if (this.$v.form_meal_booked.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendMealBookedForm()
      }
    },
    sendMealBookedForm() {
      this.$refs.submit_meal_booked.preload = true
      patchReservationsHotelsMeal(this.$route.params.id, this.form.meal.id, this.normalizeMealBookedForm())
        .finally(() => { this.$refs.submit_meal_booked.preload = false })
        .then(() => {
          this.getReservationData()
          this.$modal.hide('meal-booked-popup')
        })
    },
    normalizeMealBookedForm() {
      const normalizedForm = { ...this.form_meal_booked }

      normalizedForm.reservation_invoice = normalizedForm.reservation_invoice && normalizedForm.reservation_invoice.id ? normalizedForm.reservation_invoice.id : null
      normalizedForm.reservation_status = 'booked'
      return normalizedForm
    },
    onClickSaveMealNotAvailableButton() {
      this.$refs.submit_meal_not_available.preload = true
      patchReservationsHotelsMeal(
        this.$route.params.id,
        this.form.meal.id,
        { notes: this.meal_additional_message, reservation_status: 'not_available' }
      )
        .finally(() => { this.$refs.submit_meal_not_available.preload = false })
        .then(() => {
          this.getReservationData()
          this.$modal.hide('meal-not-available-popup')
        })
    },
    onMealOptionSelected({ code }) {
      if (code === 'not_available') {
        this.meal_additional_message = ''
        this.$modal.show('meal-not-available-popup')
      } else if (code === 'booked') {
        this.form_meal_booked = {
          reservation_invoice_amount: this.form.supplier_invoice?.amount_total,
          reservation_invoice: this.form.supplier_invoice?.attachment
        }
        this.$v.form_meal_booked.$reset()
        this.$modal.show('meal-booked-popup')
      }
    },
    onDeleteMealsCard(index) {
      this.form_request_meal.requests.splice(index, 1)
    },
    onDeleteMealsAmendmentCard(index) {
      this.form_request_amendments.meal_requests.splice(index, 1)
    },
    onClickAmendmentAddMealConfig() {
      const data = {
        __key: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        type: this.form_request_amendments.meal_type,
        date: this.form_request_amendments.meal_date,
        time: this.form_request_amendments.meal_time,
        clients: this.form_request_amendments.meal_clients,
        crew: this.form_request_amendments.meal_crew,
        is_cancelled: false,
      }

      this.form_request_amendments.meal_requests.push(data)
      this.form_request_amendments.meal_type = null
      this.form_request_amendments.meal_time = null
      this.form_request_amendments.meal_clients = null
      this.form_request_amendments.meal_crew = null
    },
    onClickRequestMealConfig() {
      this.$v.form_request_meal.$touch()
      if (this.$v.form_request_meal.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        const { requests, ...rest } = this.form_request_meal
        this.form_request_meal.requests.push({ ...rest, clients_list: [] })
        this.form_request_meal.type = null
        this.form_request_meal.time = null
        this.form_request_meal.clients = null
        this.form_request_meal.crew = null
        this.$v.form_request_meal.$reset()
      }
    },
    sendRequestMealForm() {
      this.$refs.request_meal_submit.preload = true
      postReservationsHotelsMeal(
        this.$route.params.id,
        {
          already_booked: this.form_request_meal.already_booked,
          invoice_amount: this.form_request_meal.already_booked ? this.form_request_meal.invoice_amount : null,
          requests: this.form_request_meal.requests
            .map(item => (
              { ...item, type: item.type.code, clients_list: item.clients_list.map(client => client.id) }
            ))
        }
      )
        .finally(() => { this.$refs.request_meal_submit.preload = false })
        .then(() => {
          this.$modal.hide('request-meal-popup')
          this.$notify({
            type: 'success',
            title: 'Meal created',
          })
          this.runReservationProcess()
        })
    },
    onClickAddMealButton() {
      this.form_request_meal = { date: this.form.check_out, requests: [] }
      this.$v.form_request_meal.$reset()
      this.$modal.show('request-meal-popup')
    },
    runReservationProcess() {
      getReservationsHotel(this.$route.params.id)
        .then(res => {
          this.form = res.data
          this.form.early_check_in_time = this.form.hotel.check_in
          this.form.late_check_out_time = this.form.hotel.check_out
          this.meal_plans_options = this.form.hotel.meal_plans
          document.title = `${ this.form.hotel.name } (#${ this.$route.params.id }) | Altezza Board`
          this.is_loaded = true

          getHotelsRooms(this.form.hotel.id).then(res => { this.rooms_options = res.data })
          if (this.form.lead?.client_group?.id) {
            getReservationsHotelsClients(
              this.form.lead.client_group.id,
              { check_in: this.form.check_in, check_out: this.form.check_out, hotel_id: this.form.hotel.id }
            ).then(res => { this.clients_options = res.data })
          }
        })
    },
    onRoomIconClick(type) {
      const room = this.convertToRoom(type)

      if (!this.checkExistsInArray(this.form.rooms_short, room)) {
        this.form.rooms_short.push(room)
        this.sortArrayByDate('rooms_short')
      }
    },
    convertToRoom(type) {
      return {
        accomodation_type: type,
        hotel_room: this.form.type_of_rooms,
        check_in: this.form.check_in,
        check_out: this.form.check_out,
        count: 1,
        early_check_in_time: this.form.early_check_in ? this.form.early_check_in_time : null,
        late_check_out_time: this.form.late_check_out ? this.form.late_check_out_time : null,
        meal_plan: this.form.meal_plan_second
      }
    },
    checkExistsInArray(arr, obj) {
      const removeFields = (obj) => {
        const newObj = { ...obj }
        delete newObj.id
        delete newObj.count
        delete newObj.reservation
        return newObj
      }

      const cleanedObj = removeFields(obj)

      const deepEqual = (a, b) => {
        if (a === b) return true
        if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false

        const keysA = Object.keys(a)
        const keysB = Object.keys(b)

        if (keysA.length !== keysB.length) return false

        for (const key of keysA) {
          if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false
        }

        return true
      }

      return arr.some(item => deepEqual(removeFields(item), cleanedObj))
    },
    sortArrayByDate(array) {
      this.form[array] = this.form[array].sort((a, b) => new Date(a.check_in) - new Date(b.check_in))
    },
    onShortRoomDelete(index, targetObj) {
      this.form[targetObj].splice(index, 1)
    },
    onCountChange(count, index, targetObj) {
      this.form[targetObj][index].count = +count
    },
    onShowManagerPopup() {
      this.$refs.manager_popup.preload = true
      getUsers({ group: 'reservation_supervisor,reservation_manager,reservation_assistant', is_active: true })
        .finally(() => { this.$refs.manager_popup.preload = false })
        .then(response => {
          this.managers_options = response.data
          this.$modal.show('manager')
        })
    },
    onPatchReservationAmendmentNotes() {
      patchReservationsHotelAmendmentRequest(
        this.form.amendment_request.id,
        { notes: this.form.amendment_request.notes }
      )
        .then(() => { this.getReservationData() })
    },
    onPatchReservationCancellationNotes() {
      patchReservationsHotelsCancellationRequest(
        this.form.cancellation_request.id,
        { notes: this.form.cancellation_request.notes }
      )
        .then(() => { this.getReservationData() })
    },
    onHideDeadlinePopup() {
      this.is_loaded = false
      this.$modal.hide('deadline')
      this.getReservationData()
    },
    onCancellationOptionSelected(event, is_reservation) {
      this.current_cancellation_status = event
      if (is_reservation) this.$modal.show('cancellation-reservation-accept')
      else this.changeCancellationSalesStatus()
    },
    onAmendmentOptionSelected(event, is_reservation) {
      this.current_amendment_status = event
      if (is_reservation) this.$modal.show('amendment-reservation-accept')
      else this.changeAmendmentSalesStatus()
    },
    changeAmendmentSalesStatus() {
      patchReservationsHotelAmendmentRequest(
        this.form.amendment_request.id,
        { sales_status: this.current_amendment_status.code }
      )
        .then(() => { this.getReservationData() })
    },
    changeCancellationSalesStatus() {
      patchReservationsHotelsCancellationRequest(
        this.form.cancellation_request.id,
        { sales_status: this.current_cancellation_status.code }
      )
        .then(() => { this.getReservationData() })
    },
    onCheckCancellationReservationStatusForm() {
      this.$v.cancellation_amount.$touch()
      if (this.$v.cancellation_amount.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.changeCancellationReservationStatus()
      }
    },
    changeCancellationReservationStatus() {
      this.$refs.cancellation_reservation_status.preload = true
      patchReservationsHotelsCancellationRequest(
        this.form.cancellation_request.id,
        { reservation_status: this.current_cancellation_status.code, amount: this.cancellation_amount }
      )
        .finally(() => { this.$refs.cancellation_reservation_status.preload = false })
        .then(() => {
          this.getReservationData()
          this.$modal.hide('cancellation-reservation-accept')
        })
    },
    onCheckAmendmentReservationStatusForm() {
      this.$v.amendment_amount.$touch()
      if (this.$v.amendment_amount.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.changeAmendmentReservationStatus()
      }
    },
    changeAmendmentReservationStatus() {
      this.$refs.amendment_reservation_status.preload = true
      patchReservationsHotelAmendmentRequest(
        this.form.amendment_request.id,
        { reservation_status: this.current_amendment_status.code, amount: this.amendment_amount }
      )
        .finally(() => { this.$refs.amendment_reservation_status.preload = false })
        .then(() => {
          this.getReservationData()
          this.$modal.hide('amendment-reservation-accept')
        })
    },
    onCloseAcceptCancellationReservation() {
      this.is_loaded = false
      this.$modal.hide('cancellation-reservation-accept')
      this.getReservationData()
    },
    onCloseAcceptAmendmentReservation() {
      this.is_loaded = false
      this.$modal.hide('amendment-reservation-accept')
      this.getReservationData()
    },
    onOptionSelected(e, deadline) {
      const data_reservation = {
        reservation_status: e.code,
        title: e.name,
        additional_message: ''
      }
      const data_sales = { sales_status: e.code }
      if (e.code === 'booked_provisionally') data_reservation.reservation_deadline = null
      this.onShowAcceptReservationStatusPatch(deadline ? data_reservation : data_sales, deadline)
    },
    onUpdatePaymentData() {
      this.payment_completed_loader = true

      if (this.form.rooming_list) {
        const rooms = this.form.reservation_rooms
          .map(item => (
            {
              id: item?.id,
              clients: item.clients.map(client => client.id),
            }
          ))

        postReservationsHotelRooms(this.$route.params.id, rooms)
          .then(() => {
            if (this.form.meal && this.form.meal.reservation_status?.code !== 'cancelled' && this.form.meal.requests) {
              const meal_requests = this.form.meal.requests
                .map(item => (
                  {
                    id: item?.id,
                    clients_list: item.clients_list.map(client => client.id)
                  }
                ))
              postReservationsHotelMealRequests(this.$route.params.id, meal_requests)
                .then(() => {
                  patchReservationsHotel(this.$route.params.id, { rooming_list: true })
                    .finally(() => { this.payment_completed_loader = false })
                    .then(() => {
                      this.getReservationData()
                      this.$notify({
                        type: 'success',
                        title: 'Change saved',
                      })
                    })
                    .catch(() => { this.form.rooming_list = false })
                })
                .catch(() => {
                  this.form.rooming_list = false
                  this.payment_completed_loader = false
                })
            } else {
              patchReservationsHotel(this.$route.params.id, { rooming_list: true })
                .finally(() => { this.payment_completed_loader = false })
                .then(() => {
                  this.getReservationData()
                  this.$notify({
                    type: 'success',
                    title: 'Change saved',
                  })
                })
                .catch(() => { this.form.rooming_list = false })
            }
          })
          .catch(() => {
            this.form.rooming_list = false
            this.payment_completed_loader = false
          })
      } else {
        patchReservationsHotel(this.$route.params.id, { rooming_list: false })
          .finally(() => { this.payment_completed_loader = false })
          .then(() => {
            this.getReservationData()
            this.$notify({
              type: 'success',
              title: 'Change saved',
            })
          })
          .catch(() => { this.form.rooming_list = true })
      }

      // patchReservationsHotel(
      //   +this.$route.params.id,
      //   { rooming_list: this.form.rooming_list, rooms, meal_requests }
      // )
      //   .then(() => {
      //     this.getReservationData()
      //     this.$notify({
      //       type: 'success',
      //       title: 'Change saved',
      //     })
      //     this.payment_completed_loader = false
      //   })
      //   .catch(() => {
      //     this.form.rooming_list = !this.form.rooming_list
      //     this.payment_completed_loader = false
      //   })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit_update.preload = true
      patchReservationsHotel(this.$route.params.id, this.normalizeForm())
        .finally(() => { this.$refs.submit_update.preload = false })
        .then(() => {
          this.getReservationData()
          this.$notify({
            type: 'success',
            title: 'Reservation saved',
          })
        })
    },
    normalizeForm() {
      const form = {}

      if (this.rooms_filter === 'rooms_list') {
        form.rooms = this.form.reservation_rooms
          .map(item => (
            {
              id: item?.id,
              hotel_room: item.hotel_room.id,
              accomodation_type: item.accomodation_type.code,
              clients: item.clients.map(client => client.id),
              check_in: item.check_in,
              check_out: item.check_out,
              meal_plan: item.meal_plan.code,
              early_check_in_time: item.early_check_in_time,
              late_check_out_time: item.late_check_out_time,
            }
          ))
      }
      if (this.rooms_filter === 'rooms_short') {
        form.rooms_short = this.form.rooms_short
          .map(item => (
            {
              id: item?.id,
              hotel_room: item.hotel_room.id,
              accomodation_type: item.accomodation_type.code,
              check_in: item.check_in,
              check_out: item.check_out,
              meal_plan: item.meal_plan.code,
              early_check_in_time: item.early_check_in_time,
              late_check_out_time: item.late_check_out_time,
              count: item.count
            }
          ))
      }

      return form
    },
    onCheckSendEmailForm() {
      this.$v.form_send_email.$touch()
      if (this.$v.form_send_email.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendEmailForm()
      }
    },
    sendEmailForm() {
      this.$refs.send_email_submit.preload = true
      if (this.current_action === 'amendment') {
        postReservationsHotelsAmendmentRequestsEmail(this.form.amendment_request.id, this.normalizeEmailForm())
          .finally(() => { this.$refs.send_email_submit.preload = false })
          .then(() => {
            this.$notify({
              type: 'success',
              title: 'Reservation email sent',
            })
            this.$refs.childComponent.getReservationsEmailsList()
            this.$modal.hide('send-email-popup')
          })
      } else {
        postReservationsHotelsCancellationRequestsEmail(this.form.cancellation_request.id, this.normalizeEmailForm())
          .finally(() => { this.$refs.send_email_submit.preload = false })
          .then(() => {
            this.$notify({
              type: 'success',
              title: 'Reservation email sent',
            })
            this.$refs.childComponent.getReservationsEmailsList()
            this.$modal.hide('send-email-popup')
          })
      }
    },
    normalizeEmailForm() {
      const normalizedForm = { ...this.form_send_email }

      if (normalizedForm.communication_email) normalizedForm.communication_email = normalizedForm.communication_email.id
      if (normalizedForm.email_template) normalizedForm.email_template = normalizedForm.email_template.id

      return normalizedForm
    },
    onHotelResDelete() {
      this.$refs.delete_res.preload = true
      deleteReservationsHotel(this.$route.params.id)
        .finally(() => { this.$refs.delete_res.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Reservation deleted',
          })
          this.$modal.hide('accept-delete')
          this.$router.push({ name: 'reservation-hotels-list' })
        })
    },
    onOpenSendEmailPopup(type) {
      this.current_action = type
      getReservationsEmailTemplate({
        category: type,
        type: 'hotel',
        [`${ type }_request_sales_statuses`]: this.form[`${ type }_request`].sales_status.code,
        [`${ type }_request_statuses`]: this.form[`${ type }_request`].reservation_status.code,
      })
        .then(res => {
          this.email_templates = res.data
          if (this.email_templates.length === 1) {
            this.form_send_email.email_template = this.email_templates[0]
          }
        })
      this.$modal.show('send-email-popup')
    },
    onSendRequestAmendmentForm() {
      this.$refs.request_amendment_submit.preload = true
      if (this.form.hotel.property) {
        patchReservationsHotel(this.$route.params.id, this.normalizeRequestAmendmentPropertyForm())
          .finally(() => { this.$refs.request_amendment_submit.preload = false })
          .then(() => {
            this.getReservationData()
            this.$notify({
              type: 'success',
              title: 'Request has been sent successfully',
            })
            this.$modal.hide('request-amendment')
          })
      } else {
        postReservationsHotelAmendmentRequest(this.normalizeRequestAmendmentForm())
          .finally(() => { this.$refs.request_amendment_submit.preload = false })
          .then(() => {
            this.getReservationData()
            this.$notify({
              type: 'success',
              title: 'Request has been sent successfully',
            })
            this.$modal.hide('request-amendment')
          })
      }
    },
    normalizeRequestAmendmentForm() {
      const form = {}

      form.reservation = this.$route.params.id
      form.sales_status = 'change'
      form.notes = this.form_request_amendments.notes
      form.rooms_short = this.form_request_amendments.reservation_rooms
        .filter(item => !item.is_cancelled)
        .map(item => {
          return {
            original_short_room: item.id || null,
            hotel_room: item.hotel_room.id,
            accomodation_type: item.accomodation_type.code,
            clients: item.clients ? item.clients.map(client => client.id) : null,
            early_check_in_time: item.early_check_in ? item.early_check_in_time : null,
            late_check_out_time: item.late_check_out ? item.late_check_out_time : null,
            meal_plan: item.meal_plan.code,
            check_in: item.check_in,
            check_out: item.check_out,
            count: item.count,
          }
        })
      if (this.form_request_amendments.meal_requests.length) {
        form.meals_requests = this.form_request_amendments.meal_requests
          .filter(item => !item.is_cancelled)
          .map(item => {
            return {
              date: item.date,
              time: item.time,
              clients: item.clients,
              crew: item.crew,
              type: item.type.code,
              meal_request_original: item?.id
            }
          })
      }

      return form
    },
    normalizeRequestAmendmentPropertyForm() {
      const form = {}

      form.rooms_short = this.form_request_amendments.reservation_rooms
        .filter(item => !item.is_cancelled)
        .map(item => {
          return {
            id: item.id || null,
            hotel_room: item.hotel_room.id,
            accomodation_type: item.accomodation_type.code,
            early_check_in_time: item.early_check_in ? item.early_check_in_time : null,
            late_check_out_time: item.late_check_out ? item.late_check_out_time : null,
            clients: item.clients ? item.clients.map(client => client.id) : null,
            meal_plan: item.meal_plan.code,
            check_in: item.check_in,
            check_out: item.check_out,
            count: item.count,
          }
        })

      return form
    },
    onShowRequestAmendmentsModal() {
      this.form_request_amendments = {
        reservation_rooms: [
          ...this.form.rooms_short.map(room => (
            {
              ...room,
              __key: room.id || `${Date.now()}_${Math.random().toString(36).slice(2)}`,
              early_check_in: room.early_check_in_time || false,
              late_check_out: room.late_check_out_time || false,
              is_cancelled: false
            }
          ))
        ],
        check_in: this.form.check_in,
        check_out: this.form.check_out,
        meal_date: this.form.meal ? this.form.check_out : null,
        meal_requests: this.form.meal ? [
          ...this.form.meal.requests.map(meal => (
            { ...meal, is_cancelled: false }
          ))
        ] : []
      }
      this.form_request_amendments.reservation_rooms.sort((a, b) => new Date(a.check_in) - new Date(b.check_in))
      if (this.form_request_amendments.meal_requests.length) {
        this.form_request_amendments.meal_requests.sort((a, b) => new Date(a.date) - new Date(b.date))
      }

      this.$modal.show('request-amendment')
    },
    onPatchDeadline() {
      this.$refs.submit_deadline.preload = true
      patchReservationsHotel(
        this.$route.params.id,
        {
          reservation_status: this.reservation_status_data.reservation_status,
          reservation_deadline: this.reservation_status_data.reservation_deadline || null,
          additional_message: this.reservation_status_data.additional_message,
        }
      )
        .finally(() => { this.$refs.submit_deadline.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Status changed',
          })
          this.$modal.hide('deadline')
          this.getReservationData()
        })
    },
    onPatchReservationStatus() {
      patchReservationsHotel(this.$route.params.id, this.reservation_status_data)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Status changed',
          })
          this.getReservationData()
        })
    },
    onShowAcceptReservationStatusPatch(data, deadline) {
      this.reservation_status_data = data
      if (!deadline) this.onPatchReservationStatus()
      else this.$modal.show('deadline')
    },
    onCheckManagerForm() {
      this.$v.manager.$touch()
      if (this.$v.manager.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendManagerForm()
      }
    },
    sendManagerForm() {
      this.$refs.submit_manager = true
      patchLead(this.form.lead.id, this.manager)
        .finally(() => { this.$refs.submit_manager = false })
        .then(() => {
          this.getReservationData()
          this.$modal.hide('manager')
          this.$notify({
            type: 'success',
            title: 'Reservation manager saved',
          })
        })
    },
    onRequestCancellation() {
      this.$refs.request.preload = true
      postReservationsHotelsCancellationRequest(
        {
          reservation: +this.$route.params.id,
          sales_status: this.form.cancellation_rate ? 'cancel_manually' : 'cancel',
          notes: this.notes_cancellation || null
        }
      )
        .finally(() => { this.$refs.request.preload = false })
        .then(() => {
          this.$modal.hide('accept-request-modal')
          this.getReservationData()
        })
    },
    onRequestCancellationProperty() {
      this.$refs.request.preload = true
      postReservationsHotelsHotelManagementCancel(this.$route.params.id)
        .finally(() => { this.$refs.request.preload = false })
        .then(() => {
          this.$modal.hide('accept-request-property-modal')
          this.getReservationData()
        })
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    onPatchReservationNotes() {
      patchReservationsHotel(this.$route.params.id, { notes: this.form.notes })
    },
    onChangeRoomClients(clients, index) {
      this.form.reservation_rooms[index].clients = clients
    },
    onChangeMealClients(clients, index) {
      this.form.meal.requests[index].clients_list = clients
    },
    onChangeMealRequestClients(clients, index) {
      this.form_request_meal.requests[index].clients_list = clients
    },
    onRoomDelete(index) {
      this.form.reservation_rooms.splice(index, 1)
    },
    onCardDelete(index) {
      this.form_request_amendments.reservation_rooms.splice(index, 1)
    },
    onCardCancel(index) {
      this.form_request_amendments.reservation_rooms[index].is_cancelled = true
    },
    onCardRestore(index) {
      this.form_request_amendments.reservation_rooms[index].is_cancelled = false
    },
    onCardAmendmentCancel(index) {
      this.form_request_amendments.meal_requests[index].is_cancelled = true
    },
    onCardAmendmentRestore(index) {
      this.form_request_amendments.meal_requests[index].is_cancelled = false
    },
    onCardAmendmentDelete(index) {
      this.form_request_amendments.meal_requests.splice(index, 1)
    },
    onChangeAmendmentMealFormElement(index, value, element) {
      this.form_request_amendments.meal_requests[index][element] = value
    },
    onAmendmentsRoomIconClick(type) {
      const room = this.convertToRoomAmendment(type)

      if (!this.checkExistsInArrayAmendment(this.form_request_amendments.reservation_rooms, room)) {
        this.form_request_amendments.reservation_rooms.push(room)
        this.form_request_amendments.reservation_rooms.sort((a, b) => new Date(a.check_in) - new Date(b.check_in))
      }
    },
    convertToRoomAmendment(type) {
      return {
        __key: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        accomodation_type: type,
        hotel_room: this.form_request_amendments.type_of_rooms,
        check_in: this.form_request_amendments.check_in,
        check_out: this.form_request_amendments.check_out,
        count: 1,
        early_check_in_time: this.form_request_amendments.early_check_in ? this.form_request_amendments.early_check_in_time : null,
        late_check_out_time: this.form_request_amendments.late_check_out ? this.form_request_amendments.late_check_out_time : null,
        meal_plan: this.form_request_amendments.meal_plan,
        early_check_in: this.form_request_amendments.early_check_in_time || false,
        late_check_out: this.form_request_amendments.late_check_out_time || false,
        is_cancelled: false
      }
    },
    checkExistsInArrayAmendment(arr, obj) {
      const removeFields = (obj) => {
        const newObj = { ...obj }
        delete newObj.id
        delete newObj.count
        delete newObj.reservation
        return newObj
      }

      const cleanedObj = removeFields(obj)

      const deepEqual = (a, b) => {
        if (a === b) return true
        if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false

        const keysA = Object.keys(a)
        const keysB = Object.keys(b)

        if (keysA.length !== keysB.length) return false

        for (const key of keysA) {
          if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false
        }

        return true
      }

      return arr.some(item => deepEqual(removeFields(item), cleanedObj))
    },
    getReservationData() {
      getReservationsHotel(this.$route.params.id).then(res => {
        this.form = res.data
        this.form.early_check_in_time = this.form.hotel.check_in
        this.form.late_check_out_time = this.form.hotel.check_out
        this.meal_plans_options = this.form.hotel.meal_plans
        document.title = `${ this.form.hotel.name } (#${ this.$route.params.id }) | Altezza Board`
        this.is_loaded = true
      })
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    onCopyToClipboard() {
      navigator.clipboard.writeText(location.href)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    clientTableList(clients) {
      let result = ''
      for (const key in clients) {
        result += `${ clients[key].full_name }<br>`
      }
      return result
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
  }
}
</script>
